import { useNavigate } from "react-router-dom";
import { IoMdArrowRoundBack, IoMdClose } from "react-icons/io";
import { IoMdArrowRoundForward } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { CartItem, invoice, Order } from "@/types/ApiTypes";
import toast from "react-hot-toast";
import PaginationControls from "@/components/PaginationControls";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorMessage from "@/components/ErrorMessage";
import { MdDeliveryDining } from "react-icons/md";
import { DateRange } from "react-day-picker";
import { FaEye } from "react-icons/fa";
import Loader from "@/components/loader/Loader";
import { AppConfig } from "@/utils/AppConfig";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import DateRangePicker from "./DateRangePicker";
import { formatMoney } from "@/utils/helpers";

function DriverOrdersHistory() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const locale = i18n.language;
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  // Data
  const [ordersHistory, setOrdersHistory] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs (Date)
  const dateListRef = useRef<HTMLDivElement>(null);

  // Date States
  const [chartDay, setChartDay] = useState<number | null>(null);
  const [lastMonth, setLastMonth] = useState<number | null>(null);
  const [selectedDateStart, setSelectedDateStart] = useState<string | null>(
    null,
  );
  const [selectedDateEnd, setSelectedDateEnd] = useState<string | null>(null);
  const [currentMonthStart, setCurrentMonthStart] = useState<number | null>(
    null,
  );
  const [currentMonthEnd, setCurrentMonthEnd] = useState<number | null>(null);
  const [date, setDate] = useState<DateRange | undefined>({
    from: undefined, // Default start date
    to: undefined, // Default end date (20 days after 2025-01-20)
  });

  // Order Details
  const orderPrintModalRef = useRef<HTMLDivElement>(null);
  const [orderPrintModal, setOrderPrintModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState<invoice>();

  async function getInvoice(id: string, orderType: string) {
    try {
      const options = {
        url: `${BASE_URL}/api/dashboard/orders/${id}/invoice?type=${orderType}`,
        method: "GET",

        headers: {
          Authorization: token,
        },
      };
      const response = await axios.request(options);

      if (response.data.success === true) {
        setInvoiceData(response.data.invoice);
      } else {
        closePrint();
        toast.error(response.data.error);
      }
    } catch (error) {
      console.error(error);
      closePrint();
    }
  }

  // Pagenation
  const [pageNum, setPageNum] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const handleSetPageNumber = (page: number) => {
    setPageNum(page);
    setCurrentPage(1);

  };

  // order Details functions
  function openPrint() {
    if (orderPrintModal) return;
    orderPrintModalRef.current?.classList.remove("hidden");
    orderPrintModalRef.current?.classList.add("flex");
    setOrderPrintModal(true);
  }
  function closePrint() {
    if (!orderPrintModal) return;
    orderPrintModalRef.current?.classList.remove("flex");
    orderPrintModalRef.current?.classList.add("hidden");
    setOrderPrintModal(false);
    setInvoiceData(undefined);
  }

  // Date functions

  function getTodayUnixTimestamp() {
    setChartDay(Math.floor(new Date().getTime() / 1000));
  }

  function getLast29DaysUnixTimestamp() {
    const last29Days = new Date();
    last29Days.setDate(last29Days.getDate() - 29); // Subtract 29 days
    setLastMonth(Math.floor(last29Days.getTime() / 1000)); // Convert to UNIX timestamp (seconds)
  }

  function getCurrentMonthUnixTimestamps() {
    const now = new Date(); // Current date
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // Start of the month
    const endOf29Days = new Date(startOfMonth); // Clone startOfMonth date
    endOf29Days.setDate(startOfMonth.getDate() + 28); // Add 28 days for a 29-day range

    setCurrentMonthStart(Math.floor(startOfMonth.getTime() / 1000)); // Convert to Unix timestamp
    setCurrentMonthEnd(Math.floor(endOf29Days.getTime() / 1000)); // Convert to Unix timestamp
  }

  const dateToUnix = (date: Date) => {
    return Math.floor(date.getTime() / 1000); // Convert Date to Unix timestamp (seconds)
  };

  const formatUnixTimestamp = (unixTimestamp: number | null): string => {
    if (unixTimestamp === null) {
      return "Invalid date"; // Return a default value
    }

    const date = new Date(unixTimestamp * 1000);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  function formatTimestamp(timestamp: number) {
    const date = new Date(timestamp * 1000); // Convert to milliseconds
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    // English format
    const formattedEnglish = date.toLocaleString("en-US", options);
    // Arabic format (with English numerals)
    const formattedArabicRaw = date.toLocaleString("ar-EG", options);
    const arabicNumbers = "٠١٢٣٤٥٦٧٨٩";
    const englishNumbers = "0123456789";
    const formattedArabic = formattedArabicRaw.replace(
      /[٠-٩]/g,
      (d) => englishNumbers[arabicNumbers.indexOf(d)],
    );

    if (locale === "ar") {
      return formattedArabic;
    } else {
      return formattedEnglish;
    }
  }

  const filterByDate = useCallback(
    async ({ start, end }: { start: number; end: number }) => {
      const url = `${BASE_URL}/api/dashboard/orders/history?locale=${locale}`;
      try {
        const response = await axios.get(url, {
          params: {
            startTime: formatUnixTimestamp(start),
            endTime: formatUnixTimestamp(end),
            page: currentPage,
            length: pageNum,
          },
          headers: {
            Authorization: token,
          },
        });
        setOrdersHistory(response.data.orders);
        setTotalItems(response.data.pagination.totalItems);
        setTotalPages(response.data.pagination.totalPages);
        setSelectedDateStart(unixToDate(start));
        setSelectedDateEnd(unixToDate(end));
      } catch (error) {
        console.error(error);
      } finally {
        dateListRef.current?.classList.add("hidden");
        dateListRef.current?.classList.remove("flex");
      }
    },
    [BASE_URL, currentPage, locale, pageNum, token],
  );

  const unixToDate = (unixTimestamp: number | null) => {
    if (!unixTimestamp) return "";
    const date = new Date(unixTimestamp * 1000); // Convert to milliseconds
    const month = date.getMonth() + 1; // Months are 0-based
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  };

  const getStatusDate = (
    order: Order,
    status: "finished" | "delivering" | "delivered",
  ) => {
    const date = order?.logs?.find((log) => log.to === status)?.time;
    return date ? formatTimestamp(date) : "-";
  };

  const getDriverOrdersHistory = useCallback(async () => {
    const url = `${BASE_URL}/api/dashboard/orders/history?locale=${locale}`;

    try {
      const response = await axios.get(url, {
        params: {
          startTime: formatUnixTimestamp(Math.floor(new Date().getTime() / 1000)),
          endTime: formatUnixTimestamp(Math.floor(new Date().getTime() / 1000)),
          page: currentPage,
          length: pageNum,
        },
        headers: {
          Authorization: token,
        },
      });

      if (!response.data.success) {
        const errMessage =
          response.data.error || (t("driver.unexpectedError") as string);
        setError(errMessage);
        toast.error(errMessage);
        return;
      }

      setOrdersHistory(response.data.orders);
      setTotalItems(response.data.pagination.totalItems);
      setTotalPages(response.data.pagination.totalPages);
      setSelectedDateStart(unixToDate(Math.floor(new Date().getTime() / 1000)));
    setSelectedDateEnd(unixToDate(Math.floor(new Date().getTime() / 1000)));
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data.error || (t("driver.fetchFailed") as string);
        setError(message);
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, 
  // eslint-disable-next-line
  [
    BASE_URL,
    currentMonthEnd,
    currentMonthStart,
    currentPage,
    locale,
    pageNum,
    t,
    token,
  ]);

  useEffect(() => {
    if (date?.to && date?.from && date) {
      const startUnix = dateToUnix(date.from);
      const endUnix = dateToUnix(date.to);
      if (date.to) {
        filterByDate({ start: startUnix, end: endUnix });
        dateListRef.current?.classList.add("hidden");
        dateListRef.current?.classList.remove("flex");
      }
    }
  }, [date, currentPage, filterByDate]); // Re-run when 'date' state changes

  // useEffect(() => {
  //   if (!token) return;

  //   getDriverOrdersHistory();
  // }, [getDriverOrdersHistory, token]);

  useEffect(
    () => {
      if (!selectedDateEnd && !selectedDateStart) {
        setSelectedDateStart(unixToDate(lastMonth));
        setSelectedDateEnd(unixToDate(chartDay));
      }

      getTodayUnixTimestamp();
      getLast29DaysUnixTimestamp();
      getCurrentMonthUnixTimestamps();
      if (chartDay && lastMonth) {
        getDriverOrdersHistory();
      }
    },
    // eslint-disable-next-line
    [chartDay],
  );

  if (loading)
    return (
      <div className="">
        <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
      </div>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <>
      <div className="flex flex-col gap-5">
        <button
          onClick={() => navigate("/driver-orders")}
          className="flex size-fit items-center gap-2 rounded-md p-2 duration-150 hover:bg-black/10"
        >
          {locale === "ar" ? (
            <IoMdArrowRoundForward size={26} />
          ) : (
            <IoMdArrowRoundBack size={26} />
          )}
        </button>

        {/* All Logic Inside Main */}
        <main className="flex flex-col gap-2">
          {/* Date Picker */}
          <DateRangePicker
            selectedDateStart={selectedDateStart}
            selectedDateEnd={selectedDateEnd}
            onDateChange={(range) => {
              setDate(range);
              setPageNum(10);
              setCurrentPage(1);
              if (range?.to && range?.from) {
                const startUnix = dateToUnix(range.from);
                const endUnix = dateToUnix(range.to);
                filterByDate({ start: startUnix, end: endUnix });
              }
            }}
          />

          {ordersHistory?.length ? (
            <>
              <section className="w-full">
                <div className="w-full whitespace-nowrap rounded-md border bg-[#FCFCFC] p-5">
                  <h1 className="flex items-center gap-2 font-semibold text-slate-500">
                    <MdDeliveryDining size={23} />
                    {t("driver.history")}
                    <span> ({totalItems})</span>{" "}
                  </h1>
                  <div className="mt-5">
                    <div className="thin-scrollbar overflow-x-auto border">
                      <table className="w-full bg-white text-sm text-gray-500">
                        <thead className="bg-white text-xs capitalize text-gray-700 selection:bg-none">
                          <tr className="border-b">
                            {/* finished At */}
                            <th scope="col" className="table-cell px-6 py-3">
                              {t("orders.finishedAt")}
                            </th>

                            {/* delivering At */}
                            <th scope="col" className="table-cell px-6 py-3">
                              {t("orders.deliveringAt")}
                            </th>

                            {/* delivered At */}
                            <th scope="col" className="table-cell px-6 py-3">
                              {t("orders.deliveredAt")}
                            </th>

                            {/* status */}
                            <th scope="col" className="table-cell py-3">
                              {t("orders.orderStatus")}
                            </th>

                            {/* order Details */}
                            <th className="table-cell px-6 py-3">
                              {t("orders.orderDetails")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {ordersHistory?.map((order) => (
                            <Fragment key={order?._id}>
                              <tr className="text-center">
                                {/* finished At */}
                                <td className="mx-auto table-cell px-6 py-4">
                                  {getStatusDate(order, "finished")}
                                </td>

                                {/* delivering At */}
                                <td className="mx-auto table-cell px-6 py-4">
                                  {getStatusDate(order, "delivering")}
                                </td>

                                {/* delivered At */}
                                <td className="mx-auto table-cell px-6 py-4">
                                  {getStatusDate(order, "delivered")}
                                </td>

                                {/* Status */}
                                <td className="table-cell px-6 py-4">
                                  {order?.status === "pending" &&
                                    order?.progress === null && (
                                      <div className="mx-auto w-fit rounded-2xl bg-blue-400 px-3 py-1 text-white">
                                        {t("ordersBody.pending")}
                                      </div>
                                    )}

                                  {order?.status === "accepted" &&
                                    order?.progress === null && (
                                      <div className="mx-auto w-fit rounded-2xl bg-yellow-500 px-3 py-1 text-white">
                                        {t("ordersBody.accepted")}
                                      </div>
                                    )}

                                  {order?.status === "rejected" && (
                                    <div className="mx-auto w-fit rounded-xl bg-red-500 px-2 py-[2px] text-white">
                                      {t("ordersBody.rejected")}
                                    </div>
                                  )}

                                  {order?.status === "cancelled" && (
                                    <div className="mx-auto w-fit rounded-xl bg-red-500 px-2 py-[2px] text-white">
                                      {t("ordersBody.Cancelled")}
                                    </div>
                                  )}

                                  {order?.status === "returned" && (
                                    <div className="mx-auto w-fit rounded-xl bg-purple-500 px-2 py-[2px] text-white">
                                      {t("ordersBody.returned")}
                                    </div>
                                  )}

                                  {order?.progress === "preparing" && (
                                    <div className="mx-auto w-fit">
                                      <span className="rounded-xl bg-yellow-500 px-2 py-[2px] text-white">
                                        {t("ordersBody.preparing")}
                                      </span>
                                    </div>
                                  )}

                                  {order?.progress === "finished" && (
                                    <div className="mx-auto w-fit">
                                      <span className="rounded-xl bg-indigo-500 px-2 py-[2px] text-white">
                                        {t("ordersBody.finished")}
                                      </span>
                                    </div>
                                  )}

                                  {order?.progress === "delivering" && (
                                    <div className="mx-auto w-fit">
                                      <span className="rounded-xl bg-orange-500 px-2 py-[2px] text-white">
                                        {t("ordersBody.delivering")}
                                      </span>
                                    </div>
                                  )}

                                  {order?.progress === "delivered" && (
                                    <div className="mx-auto w-fit">
                                      <span className="rounded-xl bg-green-600 px-2 py-[2px] text-white">
                                        {t("ordersBody.delivered")}
                                      </span>
                                    </div>
                                  )}
                                </td>

                                {/* Order Details */}
                                <td className="py-4">
                                  <span
                                    onClick={() => {
                                      openPrint();
                                      getInvoice(order?._id, order?.orderType);
                                    }}
                                    className="mx-auto flex w-16 cursor-pointer items-center justify-center gap-1 rounded-xl bg-blue-600 px-2 py-[2px] text-center text-[10px] text-white transition-colors duration-200 hover:bg-blue-700"
                                  >
                                    {t("ordersBody.Preview")}
                                    <FaEye className="text-sm" />
                                  </span>
                                </td>
                              </tr>
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>

              <PaginationControls
                pageNum={pageNum}
                setPageNum={handleSetPageNumber}
                totalItems={totalItems}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
              />
            </>
          ) : (
            <Alert className="border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
              <AlertTitle className="text-center text-xl">
                {t("driver.noOrdersFound")}
              </AlertTitle>
              <AlertDescription className="my-5 text-center font-semibold">
                {t("driver.noOrdersMessage")}
              </AlertDescription>
            </Alert>
          )}

          <div
            ref={orderPrintModalRef}
            className="absolute left-0 top-0 z-[9999] hidden h-full w-full items-center justify-center px-2"
          >
            <div
              onClick={closePrint}
              className="absolute left-0 top-0 z-[9991] h-full w-full bg-black opacity-85"
            ></div>
            {invoiceData ? (
              <>
                <div className="z-[9999] flex max-h-[95vh] w-full flex-col gap-2 overflow-auto rounded-lg bg-white p-5 text-black md:w-1/2 lg:w-[600px]">
                  <div
                    onClick={closePrint}
                    className="self-end text-xl text-red-600"
                  >
                    <IoMdClose className="cursor-pointer" />
                  </div>
                  <div className="flex justify-between border px-2 py-5">
                    <h2>{t("ordersBody.CustomerName")}</h2>
                    <span
                      className={`${invoiceData?.customerName !== null ? "" : "text-red-600"}`}
                    >
                      {invoiceData?.customerName !== null
                        ? invoiceData?.customerName
                        : t("ordersBody.Deleted")}
                    </span>
                  </div>

                  <div className="flex justify-between border px-2 py-5">
                    <h2>{t("ordersBody.OrderNumber")}</h2>
                    <span>{invoiceData?._id}</span>
                  </div>
                  <div className="flex justify-between border px-2 py-5">
                    <h2>{t("ordersBody.Branch")}</h2>
                    <span>
                      {" "}
                      {t(
                        invoiceData?.branch.nameEN,
                        invoiceData?.branch.nameAR,
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col gap-3 border px-2 py-5 text-center">
                    <h2 className="font-bold">
                      {t("ordersBody.OrderDetails")}
                    </h2>
                    <div className={`flex flex-col gap-1 text-[13px]`}>
                      {invoiceData?.items?.map((item: CartItem, index) => {
                        return (
                          <div
                            key={index}
                            className={`flex flex-col gap-1 py-1 ${invoiceData?.items?.length - 1 !== index && "border-b"}`}
                          >
                            <span className="font-bold" dir="ltr">
                              {t(item.nameEN, item.nameAR)} X{" "}
                              {item.quantity}{" "}
                            </span>
                            {item.notes && (
                              <div className="flex flex-col">
                                <span className="text-[14px] font-semibold">
                                  {t("ordersBody.ItemNote")}
                                </span>
                                <span className="whitespace-pre-wrap break-words">
                                  {item.notes && item.notes}
                                </span>
                              </div>
                            )}
                            {item.addons.length > 0 && (
                              <span className="flex flex-col">
                                {t("ordersBody.Addons")}{" "}
                                <span className="flex flex-col font-normal">
                                  {item.addons?.map((addon, i) => (
                                    <span key={i}>
                                      {t(addon.nameEN, addon.nameAR)}
                                    </span>
                                  ))}
                                </span>
                              </span>
                            )}
                            {item.options.length > 0 && (
                              <span className="flex flex-col">
                                {" "}
                                {t("ordersBody.Options")}
                                <span className="flex flex-col font-normal">
                                  {item.options?.map((option) =>
                                    option.options?.map((item, i) => (
                                      <span key={i}>
                                        {t(item.nameEN, item.nameAR)}
                                      </span>
                                    )),
                                  )}
                                </span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 border px-2 py-3">
                    <div className="flex justify-between">
                      <h2>{t("ordersBody.Total")}</h2>
                      <div className="flex gap-2">
                        <span dir="rtl">
                          {i18n.language && formatMoney(invoiceData?.total) }
                          {AppConfig.currency.icon()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <Loader />
            )}
          </div>
        </main>
      </div>
    </>
  );
}

export default DriverOrdersHistory;
